<template>
  <section class="section-services">
    <section-header :title="section.title">
      <a
        v-if="section.link.href && !isMobile"
        :target="section.link.target"
        :href="section.link.href"
        class="section-link"
      >{{ section.link.text }}</a>
    </section-header>
    <div
      v-if="showDescription(section) && isDesktop"
      v-dompurify-html="section.description"
      class="section-description"
    />

    <carousel
      ref="slider"
      class="services-container"
      :navigation-enabled="isDesktop"
      :navigation-next-label="''"
      :navigation-prev-label="''"
      :pagination-enabled="false"
      :per-page-custom="[[320, section.services.length], [768, 2.557], [991, 4]]"
      :scroll-per-page="false"
    >
      <slide
        v-for="(service, index) in section.services"
        :id="`service-${index}`"
        :key="index"
      >
        <service
          :title="service.title"
          :description="service.description"
          :link="service.link"
          :icon="service.images.icon"
          :new-item="service.isNew"
        />
      </slide>
    </carousel>
  </section>
</template>

<script>
  import get from 'lodash-es/get'
  import { Carousel, Slide } from 'vue-carousel'
  import { mapGetters } from 'vuex'

  export default {
    name: "ServiceSection",
    components: {
      Carousel,
      Slide,
      service: () => import(/* webpackChunkName: "service-component" */ 'containers/Pages/IndexPage/ServiceItem'),
      'section-header': () => import(/* webpackChunkName: "index-section-header" */ 'containers/Pages/IndexPage/SectionHeader'),
    },
    props: {
      section: {
        type: Object,
        required: true
      }
    },
    computed: {
      ...mapGetters('device', [
        'isDesktop',
        'isTablet',
        'isMobile'
      ])
    },
    methods: {
      showDescription (section) {
        return get(section, 'description', '') !== ''
      }
    }
  }
</script>

<style lang="scss" scoped rel="stylesheet/scss">
  @import "~assets/styles/common.scss";

  .section-services {
    padding: 28px 0;
    border: none;

    .section-description {
      @include defaultFontValues($blue-dark, 22, 34, 300);
      padding: 0 12px;
      margin-bottom: 40px;

      @include bp-mf-desktop-large {
        padding: 0;
      }
    }

    .services-container {
      display: flex;
      flex-direction: column;
      margin: 0;
      padding: 0;
      border-radius: 6px;
      box-shadow: 0 5px 16px 0 $blue-border;
      position: relative;
      background: white;
    }
  }

  ::v-deep .VueCarousel-navigation .VueCarousel-navigation-button {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    box-shadow: 0 4px 16px 0 rgba(148, 158, 179, 0.2);
    background-color: white;

    &::before {
      @include icon-font-thin();
      color: $blue-medium;
      font-size: em(18);
      content: $icon-arrow-right-thin-medium;
    }

    &.VueCarousel-navigation-prev {
      left: 30px;

      @include bp-desktop-small {
        left: 40px;
      }

      &::before {
        transform: rotate(180deg);
      }
    }

    &.VueCarousel-navigation-next {
      right: 30px;

      @include bp-desktop-small {
        right: 40px;
      }
    }

    &.VueCarousel-navigation--disabled {
      opacity: 1!important;

      &::before {
        opacity: 0.2;
      }
    }
  }

  ::v-deep .VueCarousel-inner {
    flex-direction: column;

    @include bp-mf-tablet {
      flex-direction: row;
    }
  }

  ::v-deep .VueCarousel-slide {
    flex-grow: 1;
    flex-shrink: 0;
    flex-basis: 100%;
    border-bottom: 1px solid $blue-border;

    @include bp-mf-tablet {
      flex-grow: 0;
      flex-shrink: 0;
      flex-basis: 280px;
      border-bottom: none;
    }

    &:first-child {

      .service {

        &:before {
          @include bp-mf-tablet {
            display: none;
          }
        }
      }
    }

    &:last-child {
      border-bottom: none;

      .service {
        @include bp-mf-tablet {
          border-right: none;
        }
      }
    }

    &:hover {
      @include bp-mf-tablet {
        border: 2px solid $green-base;
        flex-basis: 276px;
      }

      .service {

        .title {
          color: $blue-secondary;

          @include bp-mf-tablet {
            color: $blue-dark;
          }
        }

        @include bp-mf-tablet {
          border-right: none;
          min-width: 276px;
          width: 276px;
          padding: 34px;
          min-height: 286px;
        }

        &::before {
          display: none;
        }
      }

      + .VueCarousel-slide {

        .service {

          &::before {
            display: none;
          }
        }
      }

      .badge {
        @include bp-mf-tablet {
          left: 22px;
          top: 34px;
        }
      }
    }

    &:focus,
    &:focus-within {

      @include bp-mf-tablet {
        border: 2px solid $green-base;
        flex-basis: 276px;
      }

      .service {

        .title {
          color: $blue-secondary;

          @include bp-mf-tablet {
            color: $blue-dark;
          }
        }

        @include bp-mf-tablet {
          border-right: none;
          min-width: 276px;
          width: 276px;
          padding: 34px;
          min-height: 286px;
        }

        &::before {
          display: none;
        }
      }

      + .VueCarousel-slide {

        .service {

          &::before {
            display: none;
          }
        }
      }

      .badge {
        @include bp-mf-tablet {
          left: 22px;
          top: 34px;
        }
      }
    }
  }
</style>
